import { z } from 'zod';
import { isoTimestampSchema, uuidSchema } from './common';
import { stockMovementSchema } from './inventory';

/* Waste and shrinkage (P1 §5.3, ADR-0021). Quantities: `unit` at scale 1000. */

export const WASTE_REASONS = [
  'spoilage',
  'expired',
  'overproduction',
  'preparation_loss',
  'contamination',
  'breakage',
  'customer_return',
  'other',
] as const;
export const wasteReasonSchema = z.enum(WASTE_REASONS);
export type WasteReason = z.infer<typeof wasteReasonSchema>;

export const WASTE_STATUSES = [
  'draft',
  'submitted',
  'approved',
  'rejected',
  'posted',
  'reversed',
  'cancelled',
] as const;
export const wasteStatusSchema = z.enum(WASTE_STATUSES);
export type WasteStatus = z.infer<typeof wasteStatusSchema>;

export const wasteEvidenceSchema = z.object({
  id: uuidSchema,
  content_type: z.string(),
  size_bytes: z.number().int(),
  uploaded_by_name: z.string().nullable(),
  uploaded_at: isoTimestampSchema,
  /** Same-origin URL; the API re-checks access on every download. */
  url: z.string(),
});

export const wasteRecordSchema = z.object({
  id: uuidSchema,
  waste_number: z.string(),
  status: wasteStatusSchema,
  location_id: uuidSchema,
  location_name: z.string(),
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  base_unit: z.enum(['g', 'ml', 'pcs']),
  lot_id: uuidSchema.nullable(),
  lot_code: z.string().nullable(),
  unit: z.string(),
  unit_factor_scaled: z.number().int(),
  qty_scaled: z.number().int(),
  qty_base_scaled: z.number().int(),
  reason: wasteReasonSchema,
  note: z.string().nullable(),
  related_order_id: uuidSchema.nullable(),
  currency: z.string(),
  /** Live estimate for drafts; frozen at submission. */
  estimated_value_minor: z.number().int().nullable(),
  approval_required: z.boolean().nullable(),
  posted_value_minor: z.number().int().nullable(),
  created_by: uuidSchema,
  created_by_name: z.string().nullable(),
  submitted_by: uuidSchema.nullable(),
  submitted_by_name: z.string().nullable(),
  submitted_at: isoTimestampSchema.nullable(),
  decided_by_name: z.string().nullable(),
  decided_at: isoTimestampSchema.nullable(),
  decision_note: z.string().nullable(),
  posted_by_name: z.string().nullable(),
  posted_at: isoTimestampSchema.nullable(),
  business_date: z.iso.date().nullable(),
  movement_id: uuidSchema.nullable(),
  reversed_by_name: z.string().nullable(),
  reversed_at: isoTimestampSchema.nullable(),
  reversal_reason: z.string().nullable(),
  reversal_movement_id: uuidSchema.nullable(),
  created_at: isoTimestampSchema,
  version: z.number().int(),
  evidence: z.array(wasteEvidenceSchema),
});
export type WasteRecord = z.infer<typeof wasteRecordSchema>;

export const wasteListQuerySchema = z.object({
  status: wasteStatusSchema.optional(),
  /** Records waiting for a decision that the caller may take. */
  awaiting_my_approval: z.enum(['true', 'false']).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
});
export const wasteListResponseSchema = z.object({ items: z.array(wasteRecordSchema) });

const wasteFields = {
  location_id: uuidSchema,
  item_id: uuidSchema,
  lot_id: uuidSchema.nullable(),
  /** Standard unit or the item's pack unit; default the base unit. */
  unit: z.string().trim().min(1).max(12),
  qty_scaled: z
    .number()
    .int()
    .refine((n) => Number.isSafeInteger(n) && n > 0, 'Must be a positive safe integer'),
  reason: wasteReasonSchema,
  note: z.string().trim().min(1).max(500).nullable(),
  related_order_id: uuidSchema.nullable(),
};
export const createWasteRequestSchema = z.object({
  ...wasteFields,
  lot_id: wasteFields.lot_id.default(null),
  unit: wasteFields.unit.optional(),
  note: wasteFields.note.default(null),
  related_order_id: wasteFields.related_order_id.default(null),
});
/** Drafts only. */
export const updateWasteRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  ...z.object(wasteFields).partial().shape,
});
export const wasteActionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
});
export const wasteDecisionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  note: z.string().trim().min(1).max(300).optional(),
});
export const wasteReverseRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  reason: z.string().trim().min(3).max(300),
});

export const wastePostResultSchema = z.object({
  record: wasteRecordSchema,
  movement: stockMovementSchema,
});

// ---- Report (P1 §10: waste cost)

export const wasteReportQuerySchema = z.object({ from: z.iso.date(), to: z.iso.date() });
const bucket = z.object({
  key: z.string().nullable(),
  label: z.string(),
  value_minor: z.number().int(),
  record_count: z.number().int(),
});
export const wasteReportSchema = z.object({
  currency: z.string(),
  from: z.iso.date(),
  to: z.iso.date(),
  definitions: z.record(z.string(), z.string()),
  total_value_minor: z.number().int(),
  record_count: z.number().int(),
  by_reason: z.array(bucket),
  by_item: z.array(bucket),
  by_category: z.array(bucket),
  by_location: z.array(bucket),
  by_day: z.array(bucket),
  /** Drilldown: each posted record with its ledger movement(s), net of reversals. */
  records: z.array(
    z.object({
      id: uuidSchema,
      waste_number: z.string(),
      business_date: z.iso.date(),
      item_name: z.string(),
      reason: wasteReasonSchema,
      value_minor: z.number().int(),
      movement_ids: z.array(uuidSchema),
      reversed: z.boolean(),
    }),
  ),
});
export type WasteReport = z.infer<typeof wasteReportSchema>;
